import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaCrown,
  FaTicketAlt,
  FaInfoCircle,
  FaTimes,
  FaGem,
  FaUsers,
  FaGamepad,
  FaTrophy,
  FaChartLine,
  FaRibbon,
} from "react-icons/fa";
import CandyMachine from "../CandyShop/CandyMachine";
import BenefitsList from "../VIPLounge/BenefitsList";
import { useCandyMachine } from "../../hooks/useCandyMachine";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { CANDY_VERSIONS, VERSION_ORDER, LEVEL_CONFIG, getLevelName } from "../../data/candyConfig";
import "./CandyShopPage.css";
import { CANDY_MACHINE_BOT_BADGE_ADDRESS, CANDY_MACHINE_INTERNAL_USERS_KEY_VALUE_STORE, RADIX_API_GATEWAY_URL } from "../../../constants";

/**
 * CandyShopPage - Main page for the Candy Dispenser gacha game
 * Users insert DCKSLAP tokens to win random candy rewards
 */
const CandyShopPage = () => {
  const navigate = useNavigate();
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [nbPlayer, setNbPlayer] = useState(0);
  const [gamePlayedStats, setGamePlayedStats] = useState(0);

  const {
    level,
    gameStatus,
    lastReward,
    playRound,
    clearReward,
    resetGameStatus,
  } = useCandyMachine();

  useEffect(() => {
    const fetchNbPlayer = async () => {
      const nbPlayer = await getNbPlayer();
      setNbPlayer(nbPlayer);
    };
    fetchNbPlayer();
    const fetchGamePlayedStats = async () => {
      const gamePlayedStats = await getGamePlayedStats();
      setGamePlayedStats(gamePlayedStats);
    };
    fetchGamePlayedStats();
  }, []);

  const getNbPlayer = async () => {
    const response = await fetch(
      RADIX_API_GATEWAY_URL + "/state/key-value-store/keys",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key_value_store_address: CANDY_MACHINE_INTERNAL_USERS_KEY_VALUE_STORE,
        }),
      }
    );
    const data = await response.json();
    return data.total_count;
  };

  const getGamePlayedStats = async () => {
    let gamePlayed = 0;
    let gameWin = 0;
    let nextCursor;

    do {
      const response = await fetch(
        RADIX_API_GATEWAY_URL + "/stream/transactions",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            limit_per_page: 100,
            cursor: nextCursor,
            opt_ins: {
              receipt_output: false,
              balance_changes: true,
              manifest_instructions: true
            },
            manifest_badges_presented_filter: [
              CANDY_MACHINE_BOT_BADGE_ADDRESS,
            ],
            transaction_status_filter: "Success",
          }),
        }
      );
      const data = await response.json();
      if (data.items) {
        data.items.forEach((item) => {
          if (typeof item.manifest_instructions === "string") {
            const sendCandiesIndex = item.manifest_instructions.lastIndexOf("send_candies");
            if (sendCandiesIndex !== -1) {
              gamePlayed++;
              const fungibleChanges =
                item.balance_changes?.fungible_balance_changes;

              if (fungibleChanges && fungibleChanges.length > 0) {
                gameWin++;
              }
            }
          }
        });
      }
      nextCursor = data.next_cursor;
    } while (nextCursor);

    return {
      gamePlayed: gamePlayed,
      gameWin: gameWin,
      gameLose: gamePlayed - gameWin,
      winRate: gameWin / gamePlayed,
    };
  };

  // Handle play action
  const handlePlay = useCallback(
    async (choice) => {
      return await playRound(choice);
    },
    [playRound]
  );

  // Handle rules modal
  const handleViewRules = useCallback(() => {
    setShowRulesModal(true);
  }, []);

  const handleCloseRules = useCallback(() => {
    setShowRulesModal(false);
  }, []);

  // Navigate to VIP Lounge
  const handleGoToVIP = useCallback(() => {
    navigate("/dckslap/viplounge");
  }, [navigate]);

  const handleGoToWrapper = useCallback(() => {
    navigate("/dckslap/candywrapper");
  }, [navigate]);

  return (
    <div className="candy-shop-page">
      {/* Main Content */}
      <div className="shop-content">
        {/* Left Section - Info Cards */}
        <div className="shop-sidebar">
          {/* User Level Card */}
          <Card className="level-card">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <FaCrown className="text-[hsl(43,74%,49%)]" />
                <span>Your Level</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="level-display">
                <span className="level-number">{level}</span>
                <span className="level-label">/ {LEVEL_CONFIG.EFFECTIVE_MAX_LEVEL}</span>
              </div>
              <div className="level-name">{getLevelName(level)}</div>
              <p className="level-hint">Higher levels = Better odds!</p>
              <button className="vip-button" onClick={handleGoToVIP}>
                <FaTicketAlt />
                <span>VIP LOUNGE</span>
              </button>
            </CardContent>
          </Card>

          {/* How to Play Card */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <FaInfoCircle className="text-[hsl(43,74%,49%)]" />
                <span>How to Play</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="play-steps">
                <li>
                  <span className="step-num">1</span>
                  <span>
                    Tap the coin slot to select a Lucky Vector (1-65535)
                  </span>
                </li>
                <li>
                  <span className="step-num">2</span>
                  <span>Hit DISPENSE to insert your DCKSLAP</span>
                </li>
                <li>
                  <span className="step-num">3</span>
                  <span>Watch the capsule drop and reveal your prize!</span>
                </li>
              </ol>
              <button className="rules-button" onClick={handleViewRules}>
                <span>View Odds & Rules</span>
              </button>
            </CardContent>
          </Card>

          {/* Version Tiers Card */}
          <Card className="rarity-card">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <FaGem className="text-[hsl(280,60%,65%)]" />
                <span>Version Tiers</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rarity-list">
                {VERSION_ORDER.map((version) => {
                  const config = CANDY_VERSIONS[version];
                  return (
                    <div key={version} className="rarity-item" style={{ borderLeftColor: config.color }}>
                      <div className="rarity-capsule" style={{ background: config.gradient }}>
                        <div className="capsule-top" />
                        <div className="capsule-bottom" />
                      </div>
                      <div className="rarity-info">
                        <span className="rarity-name" style={{ color: config.color }}>{config.label}</span>
                        <span className="rarity-odds">Tier {CANDY_VERSIONS[version].tier}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Center - The Machine */}
        <div className="shop-main">
          <CandyMachine
            onPlay={handlePlay}
            gameStatus={gameStatus}
            lastReward={lastReward}
            userLevel={level}
            onClearReward={clearReward}
            onViewRules={handleViewRules}
            onResetStatus={resetGameStatus}
          />
        </div>

        {/* Right Section - More Info */}
        <div className="shop-info">
          {/* Cost Info */}
          <div className="cost-card">
            <div className="cost-header">Cost Per Play</div>
            <div className="cost-amount">
              <span className="amount">1</span>
              <span className="token">DCKSLAP</span>
              <span className="fee-badge">+ 1 XRD Fee</span>
            </div>
            <p className="cost-note">
              Requires 1 DCKSLAP + 1 XRD Protocol Fee per play.
              <br />
              <small>
                The XRD fee is used by the component to pay transaction costs.
              </small>
            </p>
          </div>

          {/* Quick Tips */}
          <div className="tips-card">
            <h4>Pro Tips</h4>
            <ul>
              <li>
                <span className="tip-icon">💡</span>
                Pick any Lucky Vector between 1 and 65535
              </li>
              <li>
                <span className="tip-icon">🎯</span>
                Some numbers have special hidden rules!
              </li>
              <li>
                <span className="tip-icon">⬆️</span>
                Level up in the VIP Lounge for better rewards
              </li>
              <li>
                <span className="tip-icon">🏆</span>
                Higher levels unlock more powerful drops
              </li>
            </ul>
          </div>

          <div className="tips-card">
            <h4>Next Stop: Candy Wrapper</h4>
            <ul>
              <li>
                <span className="tip-icon">
                  <FaRibbon />
                </span>
                Seal your loose candy into a cleaner collector presentation
              </li>
              <li>
                <span className="tip-icon">1:1</span>
                Same amount in, boutique gift-wrap identity out
              </li>
            </ul>
            <button className="vip-button" onClick={handleGoToWrapper}>
              <FaRibbon />
              <span>OPEN CANDY WRAPPER</span>
            </button>
          </div>

          {/* Game Stats Card */}
          <Card className="stats-card">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <FaChartLine className="text-[hsl(142,76%,36%)]" />
                <span>Game Stats</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="stats-grid">
                {/* Total Players */}
                <div className="stat-item">
                  <div className="stat-icon players">
                    <FaUsers />
                  </div>
                  <div className="stat-info">
                    <span className="stat-value">{nbPlayer}</span>
                    <span className="stat-label">Players</span>
                    <span className="stat-note" title="Only players Level 1 and above are counted">
                      <FaInfoCircle className="info-icon" /> Lvl 1+
                    </span>
                  </div>
                </div>

                {/* Total Games */}
                <div className="stat-item">
                  <div className="stat-icon games">
                    <FaGamepad />
                  </div>
                  <div className="stat-info">
                    <span className="stat-value">{gamePlayedStats.gamePlayed}</span>
                    <span className="stat-label">Games Played</span>
                  </div>
                </div>
              </div>

              {/* Win/Loss Ratio */}
              <div className="win-loss-section">
                <div className="ratio-header">
                  <FaTrophy className="text-[hsl(43,74%,49%)]" />
                  <span>Win Rate</span>
                </div>
                <div className="ratio-bar">
                  <div className="ratio-fill" style={{ width: `${gamePlayedStats.winRate * 100}%` }}></div>
                </div>
                <div className="ratio-stats">
                  <span className="wins">{gamePlayedStats.gameWin} Wins</span>
                  <span className="ratio-percent">{(gamePlayedStats.winRate * 100).toFixed(2)}%</span>
                  <span className="losses">{gamePlayedStats.gameLose} Losses</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Rules Modal */}
      {showRulesModal && (
        <div className="rules-modal">
          <div className="modal-backdrop" onClick={handleCloseRules} />
          <div className="modal-content">
            <button className="modal-close" onClick={handleCloseRules}>
              <FaTimes />
            </button>
            <BenefitsList currentLevel={level} />
          </div>
        </div>
      )}
    </div>
  );
};

export default CandyShopPage;
